/**
 * MONSTER TOWN — WHAT THE NEIGHBOURS DO ALL DAY. DATA. NPC CREW.
 *
 * "A DAY IN TOWN" §2: *Routines are data: a schedule table per neighbour, so a
 * new neighbour or a new venue's staff is a data drop.* This file is that drop,
 * and it is the ONLY place a rhythm is described. `src/routine/**` knows how to
 * walk a monster somewhere and how to put it to bed; it does not know what time
 * anybody gets up.
 *
 * TO ADD A NEIGHBOUR: give them a `routine` in `src/npc/roster.ts` that names one
 * of the ROLES below. TO ADD A ROLE: one `defineRoutine()` here. Neither is an
 * engine change.
 *
 * THE CLOSED SET. `RoutineActivity` is fixed by the core contract
 * (`src/core/schedule.ts`) — a table that invents its own string would compile
 * but do nothing, so the type is the guard.
 *
 * `where` IS A PLACE TAG, NOT A SCENE. The tags are listed in
 * `src/routine/places.ts`; an unknown one is not an error, it just means "around
 * here", which is exactly what a cosy town should do with a typo.
 *
 * LAW #2 LIVES HERE TOO. Nothing below is a deadline. A neighbour who is still
 * walking to the shop when the shopping hour ends simply turns round and does
 * the next thing; there is no arriving late, and nothing tells a kid so.
 */

import {
  defineRoutine,
  registerRoutines,
  needs,
  type NeedWindow,
  type RoutineTable,
} from '../core/schedule'

// ---------------------------------------------------------------------------
// the roles
// ---------------------------------------------------------------------------

/**
 * THE ORDINARY TOWNSFOLK DAY, and the shape every other role is a variation on.
 *
 * Up at half six, breakfast at home, out and about, the shop mid-morning, the
 * square at lunchtime, round to the kid's door in the afternoon, the pond when
 * the light goes gold, dinner out, a wind-down on the square, home and asleep.
 */
export const townsfolk = defineRoutine({
  id: 'role_townsfolk',
  devName: 'Ordinary neighbour',
  entries: [
    { at: 6.5, activity: 'waking', where: 'home', cue: 'mt.routine.morning' },
    { at: 7, activity: 'breakfast', where: 'home' },
    { at: 8.5, activity: 'out', where: 'street' },
    { at: 10.5, activity: 'shopping', where: 'shop' },
    { at: 12, activity: 'out', where: 'plaza' },
    { at: 14, activity: 'visiting', where: 'house' },
    { at: 16, activity: 'out', where: 'pond' },
    { at: 18, activity: 'dinner', where: 'restaurant' },
    { at: 19.5, activity: 'winddown', where: 'plaza' },
    { at: 21, activity: 'bed', where: 'home', cue: 'mt.routine.goodnight' },
    { at: 21.75, activity: 'asleep', where: 'home' },
  ],
})

/** Up with the birds, and the first light off in the evening. */
export const earlybird = defineRoutine({
  id: 'role_earlybird',
  devName: 'Early riser',
  entries: [
    { at: 5.5, activity: 'waking', where: 'home', cue: 'mt.routine.morning' },
    { at: 6, activity: 'breakfast', where: 'home' },
    { at: 7, activity: 'out', where: 'pond' },
    { at: 9, activity: 'shopping', where: 'shop' },
    { at: 11, activity: 'out', where: 'street' },
    { at: 13, activity: 'visiting', where: 'house' },
    { at: 15, activity: 'out', where: 'plaza' },
    { at: 17, activity: 'dinner', where: 'iceCream' },
    { at: 18.5, activity: 'winddown', where: 'home' },
    { at: 19.5, activity: 'bed', where: 'home', cue: 'mt.routine.goodnight' },
    { at: 20.25, activity: 'asleep', where: 'home' },
  ],
})

/** Lies in, stays out. Their window is the last one lit on the street. */
export const nightowl = defineRoutine({
  id: 'role_nightowl',
  devName: 'Night owl',
  entries: [
    { at: 0.5, activity: 'asleep', where: 'home' },
    { at: 8.5, activity: 'waking', where: 'home', cue: 'mt.routine.morning' },
    { at: 9, activity: 'breakfast', where: 'home' },
    { at: 10.5, activity: 'out', where: 'plaza' },
    { at: 13, activity: 'shopping', where: 'shop' },
    { at: 15, activity: 'visiting', where: 'house' },
    { at: 17, activity: 'out', where: 'street' },
    { at: 19, activity: 'dinner', where: 'restaurant' },
    { at: 21, activity: 'out', where: 'plaza' },
    { at: 23, activity: 'winddown', where: 'pond' },
    { at: 23.9, activity: 'bed', where: 'home', cue: 'mt.routine.goodnight' },
  ],
})

/** Behind the counter of the town shop through the middle of the day. */
export const shopkeeper = defineRoutine({
  id: 'role_shopkeeper',
  devName: 'Shopkeeper',
  entries: [
    { at: 6, activity: 'waking', where: 'home', cue: 'mt.routine.morning' },
    { at: 6.5, activity: 'breakfast', where: 'home' },
    { at: 8, activity: 'working', where: 'shop' },
    { at: 12.5, activity: 'out', where: 'plaza' },
    { at: 13.5, activity: 'working', where: 'shop' },
    { at: 17.5, activity: 'dinner', where: 'restaurant' },
    { at: 19, activity: 'winddown', where: 'plaza' },
    { at: 20.5, activity: 'bed', where: 'home', cue: 'mt.routine.goodnight' },
    { at: 21.25, activity: 'asleep', where: 'home' },
  ],
})

/** Scooping all afternoon, which is when a kid is most likely to walk in. */
export const scooper = defineRoutine({
  id: 'role_scooper',
  devName: 'Ice cream scooper',
  entries: [
    { at: 7, activity: 'waking', where: 'home', cue: 'mt.routine.morning' },
    { at: 7.5, activity: 'breakfast', where: 'home' },
    { at: 9.5, activity: 'working', where: 'iceCream' },
    { at: 16.5, activity: 'out', where: 'pond' },
    { at: 18, activity: 'dinner', where: 'home' },
    { at: 19.5, activity: 'winddown', where: 'plaza' },
    { at: 21, activity: 'bed', where: 'home', cue: 'mt.routine.goodnight' },
    { at: 21.75, activity: 'asleep', where: 'home' },
  ],
})

/** The restaurant's own monster: late start, late finish. */
export const server = defineRoutine({
  id: 'role_server',
  devName: 'Restaurant server',
  entries: [
    { at: 8, activity: 'waking', where: 'home', cue: 'mt.routine.morning' },
    { at: 8.5, activity: 'breakfast', where: 'home' },
    { at: 10, activity: 'shopping', where: 'shop' },
    { at: 11.5, activity: 'working', where: 'restaurant' },
    { at: 20.5, activity: 'winddown', where: 'plaza' },
    { at: 22, activity: 'bed', where: 'home', cue: 'mt.routine.goodnight' },
    { at: 22.75, activity: 'asleep', where: 'home' },
  ],
})

/**
 * THE ZOOKEEPER.
 *
 * The reference errand (`errand_zoo_hungry`) is offered 08:00–17:00 by
 * `npc_zookeeper`, so the table has them at work across exactly that window —
 * which is what makes "the zoo needs help at feeding time" a thing a kid can
 * walk into rather than a thing that fires at them. Venue #3 has landed, so the
 * `zoo` tag now resolves (see `ROUTINE_PLACES` below) and they really do spend
 * the working day standing by the gate. This table did not change to make that
 * true.
 */
export const zookeeper = defineRoutine({
  id: 'role_zookeeper',
  devName: 'Zookeeper',
  entries: [
    { at: 6, activity: 'waking', where: 'home', cue: 'mt.routine.morning' },
    { at: 6.5, activity: 'breakfast', where: 'home' },
    { at: 8, activity: 'working', where: 'zoo' },
    { at: 17, activity: 'out', where: 'plaza' },
    { at: 18.5, activity: 'dinner', where: 'restaurant' },
    { at: 20, activity: 'winddown', where: 'home' },
    { at: 21.5, activity: 'bed', where: 'home', cue: 'mt.routine.goodnight' },
    { at: 22.25, activity: 'asleep', where: 'home' },
  ],
})

export const ROUTINE_TABLES: readonly RoutineTable[] = [
  townsfolk,
  earlybird,
  nightowl,
  shopkeeper,
  scooper,
  server,
  zookeeper,
]

/** The role a neighbour gets when their archetype names one that does not exist. */
export const DEFAULT_ROUTINE_ID = townsfolk.id

// ---------------------------------------------------------------------------
// where those places are
// ---------------------------------------------------------------------------

/**
 * A PLACE TAG, AS DATA. `src/routine/places.ts` turns one of these into a spot a
 * monster can stand on; it knows nothing about the town.
 *
 * `landmark` is asked of the world crew on the bus (`world:landmarksRequest`),
 * so if the world moves the shop the neighbours follow it without this file
 * changing. `bearing` + `reach` are the fallback for anything the world does not
 * announce as a destination — the pond is lovely and is not a door.
 *
 * bearing: radians, 0 = east (+X), PI/2 = south (+Z). -Z is north (see
 * `src/world/layout.ts`).
 * reach:   0 = the middle of town, 1 = the edge of the walkable grid.
 *
 * A TAG WITH NO ROW IS NOT A BUG. A neighbour whose place cannot be resolved
 * simply stays where they are and potters — which is what "around here" should
 * look like (LAW #2). `zoo` was that case until venue #3 shipped; its row is
 * below now, and adding it was a data drop exactly as promised.
 */
export interface RoutinePlace {
  readonly id: string
  readonly landmark?: string
  readonly bearing?: number
  readonly reach?: number
}

export const ROUTINE_PLACES: readonly RoutinePlace[] = [
  { id: 'plaza', reach: 0 },
  { id: 'house', landmark: 'door:house', bearing: -Math.PI / 2, reach: 0.78 },
  { id: 'shop', landmark: 'door:shop', bearing: 0, reach: 0.78 },
  { id: 'iceCream', landmark: 'door:iceCream', bearing: 1.95, reach: 0.78 },
  { id: 'restaurant', landmark: 'door:restaurant', bearing: 1.19, reach: 0.78 },
  { id: 'pond', bearing: Math.PI, reach: 0.8 },
  /**
   * THE ZOO — the row this file said it was waiting for, now that venue #3 has
   * landed. The zoo's gate and its keeper are seeded into the town by the zoo
   * crew at `ZOO_TOWN_SEED` (cells 2,22 and 4,21 on a 24x24 grid at 2 units a
   * tile, i.e. the south-west front), which from the middle of town is this
   * bearing at very nearly the edge of the walkable ring.
   *
   * It is a bearing and not a `landmark` because the zoo's door is a PLACEABLE
   * the kid can move, not one of the world crew's baked front doors, so there is
   * no `door:zoo` to ask for. `walkableNear` does the rest: if a kid builds over
   * that corner the keeper stands beside their wall instead of inside it.
   */
  { id: 'zoo', bearing: 2.31, reach: 0.95 },
  { id: 'street', reach: 0.62 },
]

/**
 * How far out a neighbour's own front step sits, as a fraction of the walkable
 * half-width. 0.84 puts them on the outer pavement in front of the town's
 * buildings and cottages — far enough that a kid can see the square empty out.
 */
export const HOME_REACH = 0.84

// ---------------------------------------------------------------------------
// the kid's own two gentle needs
// ---------------------------------------------------------------------------

/**
 * WHEN THE GAME QUIETLY SUGGESTS SOMETHING. DATA.
 *
 * Read the header of `src/core/schedule.ts` before touching this. There is no
 * "unmet", there is no drain, and `bonusCoins` may not be negative — the type
 * says so and the implementation clamps anyway. A window is a stretch of the day
 * when the game is *glad* to see a kid eat or sleep, and outside it nothing at
 * all is different.
 *
 * The icons are the THING, never a face: a plate of dinner and a bed. Both exist
 * in the icon set, so nothing here can render as a missing picture (LAW #4).
 */
export const NEED_WINDOWS: readonly NeedWindow[] = [
  { id: 'hungry', fromHour: 6, toHour: 10.5, icon: 'dinner', points: 'kitchen', bonusCoins: 5 },
  { id: 'sleepy', fromHour: 20.5, toHour: 5.5, icon: 'bed', points: 'bed', bonusCoins: 5 },
]

// ---------------------------------------------------------------------------
// registration
// ---------------------------------------------------------------------------

let registered = false

/** Called once from `installRoutines()`. Idempotent. */
export function registerRoutineContent(): void {
  if (registered) return
  registered = true
  registerRoutines(ROUTINE_TABLES)
  needs.setWindows(NEED_WINDOWS)
}
